import {
  Dialog,
  Label,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Radio,
  RadioGroup,
  Transition,
} from "@headlessui/react";
import * as cadviewer from "cadviewer";
import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { CadviewerContext } from "../../context/Cadviewer.Context";
import useConfig from "../../demos/hooks/useConfig";
import { Plan, PlanDetail, View } from "../../types";
import MultiSelectCheckbox from "../MultiSelectCheckbox";

interface QuickPrintPdfProps {
  open: boolean;
  setOpen: any;
  propertyName?: string;
  propertyNumber?: string;
  plan?: Plan;
  plans?: PlanDetail[];
}

const CheckIcon = ({ className }: { className: string }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth="1.5"
      stroke="currentColor"
      className={className}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M5 13l4 4L19 7"
      />
    </svg>
  );
};

const ChevronUpDownIcon = ({ className }: { className: string }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth="1.5"
      stroke="currentColor"
      className={className}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M19 9l-7 7-7-7"
      />
    </svg>
  );
};

const CheckCircleIcon = ({ className }: { className: string }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth="1.5"
      stroke="currentColor"
      className={className}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
      />
    </svg>
  );
};

const QuickPrintPdf = ({
  open,
  setOpen,
  plan,
  plans,
  propertyNumber,
  propertyName,
}: QuickPrintPdfProps) => {
  const { config } = useConfig();

  const cancelButtonRef = useRef(null);
  const {
    currentCanvasInstance,
    views,
    currentPlanType,
    setSelectedPlan,
    setSelectedPlanDetail,
    layerColorsOnLoad,
    selectedPlan,
    selectedPlanDetail,
    signalDrawingLoadStart,
    setCurrentDrawingPath,
  } = useContext(CadviewerContext);
  const [loading, setLoading] = useState(false);
  const previousStateRef = useRef<{ plan?: Plan; detail?: PlanDetail } | null>(null);

  useEffect(() => {
    if (open && !previousStateRef.current) {
      previousStateRef.current = { plan: selectedPlan, detail: selectedPlanDetail };
    } else if (!open) {
      previousStateRef.current = null;
    }
  }, [open, selectedPlan, selectedPlanDetail]);

  const view_list =
    views.find((viewPlan) => viewPlan.planType === currentPlanType)?.views ??
    [];

  const [selectedView, setSelectedView] = useState<View | undefined>(
    !config.jsonPDFReportPreselectFirstItemInList
      ? undefined
      : view_list[0] ?? undefined
  );
  const [selectedPlans, setSelectedPlans] = useState<PlanDetail[]>([]);
  const [selectedOrientation, setSelectedOrientation] = useState<
    "portrait" | "landscape"
  >(config.pdfDefaultOrientation ?? "landscape");

  // 10.27.1
  const cvjs_processCompleted = useCallback(
    (process: string) => {
      if (["cvjs_publishPDF", "cvjs_publishMultiPagePDF"].includes(process)) {
        setLoading(false);
        setOpen(false);

        // Restore the view to what it was before the report started
        const originalPlan = previousStateRef.current?.plan;
        const originalDetail = previousStateRef.current?.detail;
        
        if (originalPlan && originalDetail) {
          setTimeout(() => {
            const svgFileLink = (
              config.propertySVGFilesLocation + originalDetail.svgFileName
            ).replace("/v2/svg/retail/", "/svg/");
            
            cadviewer.cvjs_ClearDrawing(currentCanvasInstance ?? "floorPlan");
            cadviewer.cvjs_setZoomImageWallpaper(false);
            signalDrawingLoadStart();
            cadviewer.cvjs_LoadDrawing(currentCanvasInstance ?? "floorPlan", svgFileLink);
            
            setSelectedPlan(originalPlan);
            setSelectedPlanDetail(originalDetail);
            setCurrentDrawingPath(svgFileLink);
          }, 500);
        }
      }
    },
    [
      setOpen, 
      setLoading, 
      config.propertySVGFilesLocation, 
      currentCanvasInstance, 
      signalDrawingLoadStart, 
      setSelectedPlan, 
      setSelectedPlanDetail, 
      setCurrentDrawingPath
    ]
  );

  const configParmsKey = propertyNumber ? "jsonPDFReport" : "quickPDF";

  const resolutionLists = [
    {
      id: 1,
      papersize: config[`${configParmsKey}_1_papersize`],
      resolution: config[`${configParmsKey}_1_Resolution`],
      text: config[`${configParmsKey}_1_text`],
      placeholder: config[`${configParmsKey}_1_placeholder`],
      enable: true,
    },
    {
      id: 2,
      papersize: config[`${configParmsKey}_2_papersize`],
      resolution: config[`${configParmsKey}_2_Resolution`],
      text: config[`${configParmsKey}_2_text`],
      placeholder: config[`${configParmsKey}_2_placeholder`],
      enable: true,
    },
    {
      id: 3,
      papersize: config[`${configParmsKey}_3_papersize`],
      resolution: config[`${configParmsKey}_3_Resolution`],
      text: config[`${configParmsKey}_3_text`],
      placeholder: config[`${configParmsKey}_3_placeholder`],
      enable: true,
    },
  ];

  const [selectedResolutionLists, setSelectedResolutionLists] = useState(
    resolutionLists.find(
      (resolution) =>
        resolution.resolution === config[`${configParmsKey}_default_resolution`]
    ) ?? resolutionLists[0]
  );

  const paperSizes = [
    { id: "A4", name: "A4", label: "A4 (210 x 297 mm)" },
    { id: "A3", name: "A3", label: "A3 (297 x 420 mm)" },
    { id: "A2", name: "A2", label: "A2 (420 x 594 mm)" },
    { id: "A1", name: "A1", label: "A1 (594 x 841 mm)" },
    { id: "A0", name: "A0", label: "A0 (841 x 1189 mm)" },
    { id: "A5", name: "A5", label: "A5 (148 x 210 mm)" },
    { id: "Letter", name: "Letter", label: "Letter (8.5 x 11 in)" },
    { id: "Legal", name: "Legal", label: "Legal (8.5 x 14 in)" },
    { id: "Tabloid", name: "Tabloid", label: "Tabloid (11 x 17 in)" },
    { id: "A", name: "A", label: "A (8.5 x 11 in)" },
    { id: "B", name: "B", label: "B (11 x 17 in)" },
    { id: "C", name: "C", label: "C (17 x 22 in)" },
    { id: "D", name: "D", label: "D (22 x 34 in)" },
    { id: "E", name: "E", label: "E (34 x 44 in)" },
    { id: "A+", name: "A+", label: "A+ (9 x 12 in)" },
    { id: "B+", name: "B+", label: "B+ (12 x 18 in)" },
    { id: "C+", name: "C+", label: "C+ (18 x 24 in)" },
    { id: "D+", name: "D+", label: "D+ (24 x 36 in)" },
    { id: "E+", name: "E+", label: "E+ (36 x 48 in)" },
  ];

  const [selectedPaperSize, setSelectedPaperSize] = useState(
    paperSizes.find((p) => p.id === "Letter") || paperSizes[6]
  );

  const handlePaperSizeChange = (paperSize: any) => {
    setSelectedPaperSize(paperSize);
    cadviewer.cvjs_setPrintPaperSize(paperSize.id);
  };

  const PDFReport = useCallback(() => {
    var fileList = selectedPlans.map(
      (planDetail) => config.propertySVGFilesLocation + planDetail.svgFileName
    );

    const layerSettings = ""; // for non-brookfield, we want a JSON with layers on/off, to be specified
    const lineweightfactor = 1.0;

    const currentView = selectedView?.view ?? "";
    const resolution = config.pdfReportsAsSVG
      ? "vector"
      : selectedResolutionLists.resolution ?? 0;
    const papersize = config.pdfReportsAsSVG
      ? selectedPaperSize.id
      : selectedResolutionLists.papersize ?? "letter";
    const layerViewsJSON = views;
    console.log({ selectedView, views });
    const pdfFileName =
      propertyName?.replaceAll(" ", "_") +
      "_" +
      currentView.replace(" ", "_") +
      "_" +
      papersize +
      "_" +
      (config.pdfReportsAsSVG ? "vector" : resolution + "dpi") +
      ".pdf";
    // Set orientation before generating multi-page PDF
    cadviewer.cvjs_setPrintOrientation(selectedOrientation);
    if (config.pdfReportsAsSVG) {
      cadviewer.cvjs_setPrintPaperSize(selectedPaperSize.id);
    }
    cadviewer.cvjs_publishMultiPagePDF(
      currentCanvasInstance ?? "floorPlan",
      layerSettings,
      layerViewsJSON,
      currentView,
      currentPlanType ?? "",
      layerColorsOnLoad,
      fileList,
      resolution as any,
      papersize,
      lineweightfactor,
      pdfFileName
    );
  }, [
    currentCanvasInstance,
    views,
    selectedResolutionLists.resolution,
    selectedResolutionLists.papersize,
    selectedPlans,
    propertyName,
    selectedView,
    currentPlanType,
    plan,
    layerColorsOnLoad,
    setSelectedPlan,
    selectedOrientation,
    config.pdfReportsAsSVG,
    selectedPaperSize.id,
  ]);

  const printPdf = useCallback(() => {
    if (propertyNumber) {
      if (!selectedView) {
        window.alert(config.jsonPDFErrorSelectView);
        return;
      } else if (!selectedPlans || selectedPlans.length === 0) {
        window.alert(config.jsonPDFErrorSelectPlan);
        return;
      } else if (!config.pdfReportsAsSVG && !selectedResolutionLists) {
        window.alert(config.jsonPDFErrorSelectResolution);
        return;
      } else {
        setLoading(true);
        setTimeout(() => {
          PDFReport();
        }, 100);
      }
    } else {
      if (currentCanvasInstance != null) {
        setLoading(true);
        setTimeout(() => {
          cadviewer.cvjs_setPrintOrientation(selectedOrientation);
          cadviewer.cvjs_setPrintPaperSize(
            config.pdfReportsAsSVG
              ? selectedPaperSize.id
              : selectedResolutionLists.papersize
          );
          cadviewer.cvjs_setPrintResolutionDpi(
            (config.pdfReportsAsSVG
              ? "vector"
              : selectedResolutionLists.resolution) as any
          );
          cadviewer.cvjs_setPublishPDFfilename(
            currentCanvasInstance,
            (propertyName ?? "").replaceAll(" ", "_") +
            "_" +
            (config.pdfReportsAsSVG
              ? selectedPaperSize.id
              : selectedResolutionLists.papersize) +
            "_" +
            (config.pdfReportsAsSVG
              ? "vector"
              : selectedResolutionLists.resolution + "dpi") +
            ".pdf"
          );
          cadviewer.cvjs_publishPDF(currentCanvasInstance);
        }, 100);
      }
    }
  }, [
    currentCanvasInstance,
    selectedView,
    selectedPlans,
    propertyNumber,
    selectedResolutionLists,
    PDFReport,
    propertyName,
    selectedOrientation,
    config.pdfReportsAsSVG,
    selectedPaperSize.id,
  ]);

  useEffect(() => {
    if (!open) {
      setSelectedView(undefined);
      setSelectedPlans(
        (plans ?? []).map((plan) => ({
          id: plan.planName,
          label: plan.planDisplayName,
          ...plan,
        }))
      );
      setSelectedResolutionLists(resolutionLists[0]);
      setLoading(false);
    } else {
      cadviewer.cvjs_setCallbackMethod(
        "cvjs_processCompleted",
        cvjs_processCompleted
      );
    }
  }, [open]);

  useEffect(() => {
    setSelectedPlans(
      (plans ?? []).map((plan) => ({
        id: plan.planName,
        label: plan.planDisplayName,
        ...plan,
      }))
    );
  }, [plans, open]);

  console.log({ selectedPlans });

  const renderSimplePrint = () => {
    return (
      <RadioGroup
        value={selectedResolutionLists}
        onChange={setSelectedResolutionLists}
        className="mt-2 grid grid-cols-1 gap-y-6 sm:grid-cols-1 sm:gap-x-4"
      >
        {resolutionLists.map((resolutionList) => (
          <Radio
            key={resolutionList.id}
            value={resolutionList}
            aria-label={resolutionList.text}
            // aria-resolution={`${resolutionList.text} : ${resolutionList.placeholder}`}
            className="group relative flex cursor-pointer rounded-lg border border-gray-300 bg-white p-4 shadow-sm focus:outline-none data-[focus]:border-primary-600 data-[focus]:ring-2 data-[focus]:ring-primary-600"
          >
            <span className="flex flex-1">
              <span className="flex flex-col">
                <span className="block text-sm font-medium text-gray-900 capitalize">
                  {resolutionList.text}
                </span>
                <span className="mt-1 flex items-center text-sm text-gray-500">
                  {resolutionList.placeholder}
                </span>
              </span>
            </span>
            <CheckCircleIcon
              aria-hidden="true"
              className="h-5 w-5 text-primary-600 [.group:not([data-checked])_&]:invisible"
            />
            <span
              aria-hidden="true"
              className="pointer-events-none absolute -inset-px rounded-lg border-2 border-transparent group-data-[focus]:border group-data-[checked]:border-primary-600"
            />
          </Radio>
        ))}
      </RadioGroup>
    );
  };

  const renderPaperSizeSelector = () => (
    <div className="mb-3 flex items-center justify-between">
      <span className="text-sm font-medium text-gray-900">Paper Size</span>
      <div className="w-[15.5rem]">
        <Listbox value={selectedPaperSize} onChange={handlePaperSizeChange}>
          <div className="relative cursor-pointer">
            <ListboxButton className="relative w-full cursor-pointer rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:text-sm/6">
              <span className="inline-flex w-full truncate">
                <span className="truncate">{selectedPaperSize.label}</span>
              </span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronUpDownIcon aria-hidden="true" className="size-5 text-gray-400" />
              </span>
            </ListboxButton>

            <ListboxOptions
              transition
              style={{ zIndex: 10000002 }}
              className="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in sm:text-sm"
            >
              {paperSizes.map((size) => (
                <ListboxOption
                  key={size.id}
                  value={size}
                  className="group relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-primary-600 data-[focus]:text-white"
                >
                  <div className="flex">
                    <span className="truncate font-normal group-data-[selected]:font-semibold">
                      {size.label}
                    </span>
                  </div>

                  <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-primary-600 group-data-[focus]:text-white [.group:not([data-selected])_&]:hidden">
                    <CheckIcon aria-hidden="true" className="size-5" />
                  </span>
                </ListboxOption>
              ))}
            </ListboxOptions>
          </div>
        </Listbox>
      </div>
    </div>
  );

  const renderOrientationSelector = () => (
    <div className="mb-3 flex items-center justify-between">
      <span className="text-sm font-medium text-gray-900">
        {config.pdfOrientationLabel ?? "Orientation"}
      </span>
      <RadioGroup
        value={selectedOrientation}
        onChange={setSelectedOrientation}
        className="flex gap-2"
      >
        <Radio
          value="portrait"
          className={`relative flex cursor-pointer rounded-md border bg-white px-3 py-1.5 focus:outline-none ${selectedOrientation === "portrait"
            ? "border-primary-600 ring-1 ring-primary-600"
            : "border-gray-300"
            }`}
        >
          <span className="flex items-center gap-1.5">
            <span
              className={`h-4 w-4 flex items-center justify-center rounded-full border-2 ${selectedOrientation === "portrait"
                ? "border-primary-600 bg-primary-600"
                : "border-gray-300 bg-white"
                }`}
            >
              {selectedOrientation === "portrait" && (
                <svg
                  className="h-2 w-2 text-white"
                  fill="currentColor"
                  viewBox="0 0 12 12"
                >
                  <circle cx="6" cy="6" r="6" />
                </svg>
              )}
            </span>
            <span className="text-sm text-gray-900">
              {config.pdfOrientationPortraitLabel ?? "Portrait"}
            </span>
          </span>
        </Radio>
        <Radio
          value="landscape"
          className={`relative flex cursor-pointer rounded-md border bg-white px-3 py-1.5 focus:outline-none ${selectedOrientation === "landscape"
            ? "border-primary-600 ring-1 ring-primary-600"
            : "border-gray-300"
            }`}
        >
          <span className="flex items-center gap-1.5">
            <span
              className={`h-4 w-4 flex items-center justify-center rounded-full border-2 ${selectedOrientation === "landscape"
                ? "border-primary-600 bg-primary-600"
                : "border-gray-300 bg-white"
                }`}
            >
              {selectedOrientation === "landscape" && (
                <svg
                  className="h-2 w-2 text-white"
                  fill="currentColor"
                  viewBox="0 0 12 12"
                >
                  <circle cx="6" cy="6" r="6" />
                </svg>
              )}
            </span>
            <span className="text-sm text-gray-900">
              {config.pdfOrientationLandscapeLabel ?? "Landscape"}
            </span>
          </span>
        </Radio>
      </RadioGroup>
    </div>
  );

  const renderJsonPrint = () => {
    return (
      <div>
        <Listbox value={selectedView} onChange={setSelectedView}>
          <Label className="block text-sm/6 font-medium text-gray-900">
            {config.jsonPDFReportSelectViewLabel}
          </Label>
          <span className="block text-xs font-medium text-gray-500 mt-1">
            {config.jsonPDFReportSelectViewSubtitle}
          </span>
          <div className="relative mt-2 cursor-pointer">
            <ListboxButton className="relative w-full cursor-pointer rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:text-sm/6">
              {selectedView ? (
                <span className="inline-flex w-full truncate">
                  <span className="truncate">{selectedView?.view}</span>
                </span>
              ) : (
                <span className={"inline-flex w-full truncate"}>
                  {config.jsonPDFReportSelectViewLabel}
                </span>
              )}
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronUpDownIcon
                  aria-hidden="true"
                  className="size-5 text-gray-400"
                />
              </span>
            </ListboxButton>

            <ListboxOptions
              transition
              style={{ zIndex: 10000002 }}
              className="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in sm:text-sm"
            >
              {view_list.map((view) => (
                <ListboxOption
                  key={view.view}
                  value={view}
                  className="group relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-primary-600 data-[focus]:text-white"
                >
                  <div className="flex">
                    <span className="truncate font-normal group-data-[selected]:font-semibold">
                      {view.view}
                    </span>
                  </div>

                  <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-primary-600 group-data-[focus]:text-white [.group:not([data-selected])_&]:hidden">
                    <CheckIcon aria-hidden="true" className="size-5" />
                  </span>
                </ListboxOption>
              ))}
            </ListboxOptions>
          </div>
        </Listbox>

        <span className="block text-sm font-medium text-gray-900 mt-2">
          {config.jsonPDFReportSelectPlanLabel}
        </span>
        <span className="block text-xs font-medium text-gray-500 mt-1">
          {config.jsonPDFReportSelectPlanSubtitle}
        </span>
        <div className="relative mt-2 cursor-pointer">
          <MultiSelectCheckbox
            options={(plans ?? []).map((plan) => ({
              id: plan.planName,
              label: plan.planDisplayName,
              ...plan,
            }))}
            onChange={setSelectedPlans}
            defaultSelectedOptions={selectedPlans}
            itemName={"plan"}
            placeholder={config.jsonPDFReportSelectPlanLabel}
            keyExtractor={(plan) => plan.planName}
            labelExtractor={(plan) => plan.planDisplayName}
          />
        </div>

        {!config.pdfReportsAsSVG && (
          <>
            <span className="block text-sm/6 font-medium text-gray-900 mt-2">
              {config.jsonPDFReportSelectResolutionLabel}
            </span>
            <span className="block text-xs font-medium text-gray-500 mt-1">
              {config.jsonPDFReportSelectResolutionSubtitle}
            </span>
          </>
        )}
        {/*    Resolution */}
        {/*    <Listbox value={selectedResolutionLists} onChange={setSelectedResolutionLists}>*/}
        {/*        <Label className="block text-sm/6 font-medium text-gray-900 mt-2">{config.jsonPDFReportSelectResolutionLabel}</Label>*/}
        {/*        <div className="relative mt-2 cursor-pointer">*/}
        {/*            <ListboxButton className="relative w-full cursor-pointer rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:text-sm/6">*/}
        {/*                {selectedResolutionLists ? <span className="inline-flex w-full truncate">*/}
        {/*                    <span className="truncate">{selectedResolutionLists.text}</span>*/}
        {/*                    </span> : <span className={"inline-flex w-full truncate"}>{config.jsonPDFReportSelectResolutionLabel}</span>*/}
        {/*                    }*/}
        {/*                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">*/}
        {/*                <ChevronUpDownIcon aria-hidden="true" className="size-5 text-gray-400" />*/}
        {/*              </span>*/}
        {/*            </ListboxButton>*/}

        {/*            <ListboxOptions*/}
        {/*                transition*/}
        {/*                className="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in sm:text-sm"*/}
        {/*            >*/}
        {/*                {resolutionLists.filter((resolutionList) => resolutionList.enable).map((resolutionList) => (*/}
        {/*                    <ListboxOption*/}
        {/*                        key={resolutionList.id}*/}
        {/*                        value={resolutionList}*/}
        {/*                        className="group relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-primary-600 data-[focus]:text-white"*/}
        {/*                    >*/}
        {/*                        <div className="flex flex-col">*/}
        {/*                            <span className="truncate font-normal group-data-[selected]:font-semibold">{resolutionList.text}</span>*/}
        {/*                            <span className="truncate text-gray-500 group-data-[focus]:text-primary-200">*/}
        {/*                              {resolutionList.placeholder}*/}
        {/*                            </span>*/}
        {/*                        </div>*/}

        {/*                        <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-primary-600 group-data-[focus]:text-white [.group:not([data-selected])_&]:hidden">*/}
        {/*                            <CheckIcon aria-hidden="true" className="size-5" />*/}
        {/*                        </span>*/}
        {/*                    </ListboxOption>*/}
        {/*                ))}*/}
        {/*            </ListboxOptions>*/}
        {/*        </div>*/}
        {/*    </Listbox>*/}
        {!config.pdfReportsAsSVG && renderSimplePrint()}
      </div>
    );
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative"
        style={{ zIndex: 10000002 }}
        initialFocus={cancelButtonRef}
        onClose={setOpen}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div
          style={{ zIndex: 10000003 }}
          className="fixed inset-0 w-screen overflow-y-auto"
        >
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-visible rounded-lg bg-white px-3 pb-3 pt-3 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-3">
                {loading ? (
                  <div
                    className={
                      "mt-4 min-h-[100px] h-full w-full flex justify-center items-center flex-col gap-4 text-gray-500"
                    }
                  >
                    <svg
                      className="animate-spin -ml-1 mr-3 h-10 w-10 text-gray-500"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span className={"text-gray-500"}>
                      {config.quickPDFProcessingText}
                    </span>
                    <span className={"text-gray-400 text-xs mt-1 max-w-xs text-center"}>
                      (Heavy processing may cause the animation to pause momentarily)
                    </span>
                  </div>
                ) : (
                  <>
                    <div>
                      <div className="">
                        <Dialog.Title as="h3" className="relative">
                          <div className={"flex gap-2 items-center"}>
                            <a
                              href={"https://cadviewer.com"}
                              target={"_blank"}
                              rel="noreferrer"
                            >
                              <img
                                src={"/cvlogo.svg"}
                                alt="CADViewer Logo"
                                className={`h-8`}
                              />
                            </a>
                            <div className={"flex flex-col gap-3"}>
                              <span
                                className={
                                  "text-xl font-semibold leading-6 text-gray-900"
                                }
                              >
                                {propertyNumber
                                  ? `${config.enableJsonPDFReportModalTitle} - ${propertyName}`
                                  : `${config.enableQuickPdfPrintModalTitle}`}
                              </span>
                            </div>
                          </div>
                          <button
                            type="button"
                            className="top-1 right-1 absolute rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-600"
                            onClick={() => setOpen(false)}
                          >
                            <span className="absolute -inset-2.5" />
                            <span className="sr-only">Close panel</span>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                              className="w-6 h-6"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        </Dialog.Title>

                        <div className={`mt-4 ${(!propertyNumber && config.pdfReportsAsSVG) ? "" : "min-h-[300px]"}`}>
                          <fieldset>
                            <legend className="text-sm/6 font-semibold text-gray-900">
                              {propertyNumber
                                ? config.enableJsonPDFReportModalSubTitle
                                : config.enableQuickPdfPrintModalSubTitle}
                            </legend>
                            <div className={"mt-4"}>
                              {renderOrientationSelector()}
                              {config.pdfReportsAsSVG && renderPaperSizeSelector()}
                              {propertyNumber
                                ? renderJsonPrint()
                                : !config.pdfReportsAsSVG && renderSimplePrint()}
                            </div>
                          </fieldset>
                        </div>
                      </div>
                    </div>
                    <div className="mt-5 flex justify-between gap-4">
                      <button
                        type="button"
                        className="inline-flex w-full justify-center rounded-md border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 sm:col-start-1"
                        onClick={() => {
                          setOpen(false);
                        }}
                        ref={cancelButtonRef}
                        disabled={loading}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="inline-flex w-full justify-center rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 sm:col-start-2"
                        onClick={printPdf}
                        disabled={loading}
                      >
                        {loading && (
                          <svg
                            className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                        )}
                        {config.jsonPDFPrintTextButton}
                      </button>
                    </div>
                  </>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default QuickPrintPdf;
